import React from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import PropTypes from 'prop-types'

import { useOnClickOutside } from './lib/OnClickOutside'
import { Loading } from './common/Loading'
import { Landing } from './modules/website/Landing'
import { Error404 } from './common/Error404'
import { Logout } from './common/Logout'
import { useGetQuery } from './lib/api/get'
import { useAuthStore } from './store/auth'
import { ModuleBuilder } from './modules/ModuleBuilder'
import { getUser } from './lib/authentication'
import { CookieConsentComp } from './common/CookieConsent'
import { Login } from './common/Login'
import { NoInternetConnection } from './common/NoInternetConnection'

import PageEditor from './modules/projects/components/PageEditor'
import NavbarEditor from './modules/projects/components/NavbarEditor'
import FooterEditor from './modules/projects/components/FooterEditor'
import { Preview } from './modules/preview/Preview'

import './assets/css/bootstrap.min.css'
import './assets/css/app.min.css'

/**
 * App Component
 *
 * The root component for the application. It manages the application state, handles authentication, and sets up routing for the app.
 *
 * @module App
 * @description The root component for the application. It manages the application state, handles authentication, and sets up routing for the app.
 * @example
 * return (
 *   <App
 *     envData={envData}
 *     isLocalEnvironment={isLocalEnvironment}
 *   />
 * );
 *
 * @param {Object} envData - Environment-specific configuration data used for rendering.
 * @param {string} isLocalEnvironment - Indicates whether the environment is local.
 *
 * @returns {React.Element} The main application component with routing and conditional rendering based on authentication and application data.
 */
function App({ envData, isLocalEnvironment }) {
  const [toggleLoginModal, setToggleLoginModal] = React.useState(false)
  const [activeOrgRoutes, setActiveOrgRoutes] = React.useState(
    sessionStorage.getItem('activeRoutes')
      ? JSON.parse(sessionStorage.getItem('activeRoutes'))
      : null,
  )
  const sideLoginModalRef = React.useRef()
  useOnClickOutside(sideLoginModalRef, () => setToggleLoginModal(false))

  const getAppData = useGetQuery({ apiURL: isLocalEnvironment, apiEndpoint: 'appData' })
  const authDetails = useAuthStore((state) => state)
  const { setAuthentication, setOrgRole } = useAuthStore()
  React.useEffect(() => {
    getUser({
      isLocalEnvironment,
      setAuthentication,
      setOrgRole,
      authDetails,
      envData,
      setActiveOrgRoutes,
      setIsDefaultOrg,
    })
  }, [isLocalEnvironment, setAuthentication, authDetails])

  const [isDefaultOrg, setIsDefaultOrg] = React.useState(
    (authDetails.dependeciesFormAPIS?.userSettings?.formData?.userSettings &&
      JSON.parse(authDetails.dependeciesFormAPIS?.userSettings?.formData?.userSettings)) ||
      null,
  )

  let getProjects, getThemes, getImages, getNavbars, getFooters, getPages, getBlocks
  const queryParams = new URLSearchParams(window.location.search)
  if (envData.REACT_APP_TYPE === 'builder') {
    if (localStorage.getItem('sessionStorage')) {
      const storage = localStorage.getItem('sessionStorage')
      sessionStorage.setItem('auth', storage)
      localStorage.removeItem('sessionStorage')
      const reloadCount = sessionStorage.getItem('reloadCount')
      if (reloadCount < 2) {
        sessionStorage.setItem('reloadCount', String(reloadCount + 1))
        window.location.reload()
      } else {
        sessionStorage.removeItem('reloadCount')
      }
    }
    getProjects = useGetQuery({
      apiURL: isLocalEnvironment + '/getData',
      apiEndpoint: 'websiteProjects',
      isDynamic: true,
      params: `&isDependency=true${queryParams.get('targetTenantId') ? '&targetTenantId=' + queryParams.get('targetTenantId') : ''}${queryParams.get('targetAppId') ? '&targetAppId=' + queryParams.get('targetAppId') : ''}${queryParams.get('targetOrgId') ? '&targetOrgId=' + queryParams.get('targetOrgId') : ''}`,
      enabled:
        (authDetails.loggedIn || queryParams.get('targetTenantId')) &&
        envData.REACT_APP_TYPE === 'builder'
          ? true
          : false,
    })
    getPages = useGetQuery({
      apiURL: isLocalEnvironment + '/getData',
      apiEndpoint: 'websitePages',
      isDynamic: true,
      params: `&isDependency=true${queryParams.get('targetTenantId') ? '&targetTenantId=' + queryParams.get('targetTenantId') : ''}${queryParams.get('targetAppId') ? '&targetAppId=' + queryParams.get('targetAppId') : ''}${queryParams.get('targetOrgId') ? '&targetOrgId=' + queryParams.get('targetOrgId') : ''}`,
      enabled:
        (authDetails.loggedIn || queryParams.get('targetTenantId')) &&
        envData.REACT_APP_TYPE === 'builder'
          ? true
          : false,
    })
    getThemes = useGetQuery({
      apiURL: isLocalEnvironment + '/getData',
      apiEndpoint: 'themes',
      isDynamic: true,
      params: '&isDependency=true',
      enabled:
        (authDetails.loggedIn || queryParams.get('targetTenantId')) &&
        envData.REACT_APP_TYPE === 'builder'
          ? true
          : false,
    })
    getImages = useGetQuery({
      apiURL: isLocalEnvironment + '/getData',
      apiEndpoint: 'image',
      isDynamic: true,
      enabled:
        (authDetails.loggedIn || queryParams.get('targetTenantId')) &&
        envData.REACT_APP_TYPE === 'builder'
          ? true
          : false,
    })
    getNavbars = useGetQuery({
      apiURL: isLocalEnvironment + '/getData',
      apiEndpoint: 'themeNavbars',
      isDynamic: true,
      params: '&isDependency=true',
      enabled:
        (authDetails.loggedIn || queryParams.get('targetTenantId')) &&
        envData.REACT_APP_TYPE === 'builder'
          ? true
          : false,
    })
    getFooters = useGetQuery({
      apiURL: isLocalEnvironment + '/getData',
      apiEndpoint: 'themeFooters',
      isDynamic: true,
      params: '&isDependency=true',
      enabled:
        (authDetails.loggedIn || queryParams.get('targetTenantId')) &&
        envData.REACT_APP_TYPE === 'builder'
          ? true
          : false,
    })
    getBlocks = useGetQuery({
      apiURL: isLocalEnvironment + '/getData',
      apiEndpoint: 'themeBlocks',
      isDynamic: true,
      params: '&isDependency=true',
      enabled:
        (authDetails.loggedIn || queryParams.get('targetTenantId')) &&
        envData.REACT_APP_TYPE === 'builder'
          ? true
          : false,
    })
  }

  if (
    (envData.REACT_APP_TYPE === 'builder' &&
      (getProjects.isLoading ||
        getThemes.isLoading ||
        getImages.isLoading ||
        getNavbars.isLoading ||
        getFooters.isLoading ||
        getPages.isLoading ||
        getBlocks.isLoading)) ||
    getAppData.isLoading
  ) {
    return (
      <div id="layout-wrapper" data-testid="appLoading">
        <Loading envData={envData} />
        <div className="trtui-vertical-overlay" />
      </div>
    )
  }
  let allData
  if (envData.REACT_APP_TYPE === 'builder') {
    allData = {
      projects: getProjects.data?.formData,
      pages: getPages.data?.formData,
      themes: getThemes.data?.formData,
      images: getImages.data?.formData,
      themeNavbars: getNavbars.data?.formData,
      themeFooters: getFooters.data?.formData,
      blocks: getBlocks.data?.formData,
    }
  }
  const asstImg = []
  if (getImages.data?.formData.length) {
    getImages.data?.formData?.map((i) => {
      const newImg = i.fileUrl.replace('%2F', '/')
      asstImg.push(newImg)
    })
    localStorage.setItem('builderAssets', JSON.stringify(asstImg))
  }
  if (getImages.data?.formData?.data) {
    getImages.data?.formData?.data?.map((i) => {
      const newImg = i.fileUrl.replace('%2F', '/')
      asstImg.push(newImg)
    })
    localStorage.setItem('builderAssets', JSON.stringify(asstImg))
  }

  const appDataParsed = getAppData.data?.formData
  return (
    <>
      <NoInternetConnection>
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              exact
              element={
                <Login
                  envData={envData}
                  isLocalEnvironment={isLocalEnvironment}
                  isLoggedIn={authDetails.loggedIn}
                />
              }
            />
            {appDataParsed.routesData?.length > 0 ? (
              appDataParsed.routesData.map((route, routeIndex) => (
                // Public website routes are rendered
                <Route
                  path={route.routePath}
                  key={`${routeIndex}${route.pageName}`}
                  exact
                  element={
                    <Login
                      envData={envData}
                      isLocalEnvironment={isLocalEnvironment}
                      isLoggedIn={authDetails.loggedIn}
                    />
                  }
                />
              ))
            ) : (
              // Route to landing page when home page is not available
              <Route
                path="/"
                exact
                element={
                  <Landing
                    envData={envData}
                    isLocalEnvironment={isLocalEnvironment}
                    isLoggedIn={authDetails.loggedIn}
                  />
                }
              />
            )}
            {/* Backend module routes are rendered if user is logged in */}
            {authDetails.loggedIn &&
              appDataParsed.routesData?.length > 0 &&
              appDataParsed.routesData.map((mod, modInd) => (
                <Route
                  key={`${modInd}${mod.component}${mod.pageName}`}
                  path={`console/${mod.routePath}`}
                  element={
                    <ModuleBuilder
                      key={`${mod}${mod.pageName}`}
                      routeData={mod}
                      appDataParsed={appDataParsed}
                      allData={allData}
                      envData={envData}
                      isLocalEnvironment={isLocalEnvironment}
                      authDetails={authDetails}
                      setToggleLoginModal={setToggleLoginModal}
                      toggleLoginModal={toggleLoginModal}
                      sideLoginModalRef={sideLoginModalRef}
                      setActiveOrgRoutes={setActiveOrgRoutes}
                      activeOrgRoutes={activeOrgRoutes}
                      isDefaultOrg={isDefaultOrg}
                      setIsDefaultOrg={setIsDefaultOrg}
                    />
                  }
                />
              ))}
            {activeOrgRoutes !== null &&
              activeOrgRoutes.formData?.Pages?.map((mod, modInd) =>
                envData.REACT_APP_APP_ISONLINE === '1' && mod.isVisible ? (
                  <Route
                    key={`${modInd}${mod.component}${mod.pageName}`}
                    path={`organization/${mod.routePath}`}
                    element={
                      <ModuleBuilder
                        key={`${mod}${mod.pageName}`}
                        routeData={mod}
                        appDataParsed={appDataParsed}
                        allData={allData}
                        envData={envData}
                        isLocalEnvironment={isLocalEnvironment}
                        authDetails={authDetails}
                        setToggleLoginModal={setToggleLoginModal}
                        toggleLoginModal={toggleLoginModal}
                        sideLoginModalRef={sideLoginModalRef}
                        setActiveOrgRoutes={setActiveOrgRoutes}
                        activeOrgRoutes={activeOrgRoutes}
                        isDefaultOrg={isDefaultOrg}
                        setIsDefaultOrg={setIsDefaultOrg}
                      />
                    }
                  />
                ) : (
                  <Route
                    key={`${modInd}${mod.component}${mod.pageName}`}
                    path={`organization/${mod.routePath}`}
                    element={
                      <ModuleBuilder
                        key={`${mod}${mod.pageName}`}
                        routeData={mod}
                        appDataParsed={appDataParsed}
                        allData={allData}
                        envData={envData}
                        isLocalEnvironment={isLocalEnvironment}
                        authDetails={authDetails}
                        setToggleLoginModal={setToggleLoginModal}
                        toggleLoginModal={toggleLoginModal}
                        sideLoginModalRef={sideLoginModalRef}
                        setActiveOrgRoutes={setActiveOrgRoutes}
                        activeOrgRoutes={activeOrgRoutes}
                        isDefaultOrg={isDefaultOrg}
                        setIsDefaultOrg={setIsDefaultOrg}
                      />
                    }
                  />
                ),
              )}
            <Route
              key="dashboard"
              path="console/dashboard"
              element={
                <ModuleBuilder
                  appDataParsed={appDataParsed}
                  envData={envData}
                  isLocalEnvironment={isLocalEnvironment}
                  authDetails={authDetails}
                  setToggleLoginModal={setToggleLoginModal}
                  toggleLoginModal={toggleLoginModal}
                  sideLoginModalRef={sideLoginModalRef}
                  routeData={{ apiEndPointSchema: JSON.stringify({}), component: 'Dashboard' }}
                  setActiveOrgRoutes={setActiveOrgRoutes}
                  activeOrgRoutes={activeOrgRoutes}
                  isDefaultOrg={isDefaultOrg}
                  setIsDefaultOrg={setIsDefaultOrg}
                />
              }
            />
            {envData.REACT_APP_TYPE === 'builder' && (
              <>
                <Route
                  key="pageEditor"
                  path="console/page-editor"
                  element={
                    <PageEditor
                      allData={allData}
                      envData={envData}
                      authDetails={authDetails}
                      isLocalEnvironment={isLocalEnvironment}
                    />
                  }
                />
                <Route
                  key="navbarEditor"
                  path="console/navbar-editor"
                  element={
                    <NavbarEditor
                      allData={allData}
                      envData={envData}
                      authDetails={authDetails}
                      isLocalEnvironment={isLocalEnvironment}
                    />
                  }
                />
                <Route
                  key="footerEditor"
                  path="console/footer-editor"
                  element={
                    <FooterEditor
                      allData={allData}
                      envData={envData}
                      authDetails={authDetails}
                      isLocalEnvironment={isLocalEnvironment}
                    />
                  }
                />
                <Route
                  key="preview"
                  path="console/preview"
                  element={
                    <Preview
                      allData={allData}
                      isLocalEnvironment={isLocalEnvironment}
                      authDetails={authDetails}
                    />
                  }
                />
              </>
            )}
            <Route
              key="logout"
              path="console/logout"
              element={<Logout isLocalEnvironment={isLocalEnvironment} />}
            />
            {authDetails.loggedIn ? (
              <Route key="not-found" path="*" element={<Error404 pageNotFound={true} />} />
            ) : (
              <Route
                path="*"
                exact
                element={
                  <Login
                    envData={envData}
                    isLocalEnvironment={isLocalEnvironment}
                    isLoggedIn={authDetails.loggedIn}
                  />
                }
              />
            )}
          </Routes>
        </BrowserRouter>
        {/* {appDataParsed.appSettings['appSettings-cookieConsentRequired'] && (
        <CookieConsentComp appSettings={appDataParsed.appSettings} />
      )} */}
        <CookieConsentComp />
      </NoInternetConnection>
    </>
  )
}

export default App

App.propTypes = {
  envData: PropTypes.object.isRequired,
  isLocalEnvironment: PropTypes.string.isRequired,
}
